import { FormJourneyItem } from "@plura/shared/types"
import { truncate } from "@plura/shared/utils"

interface HiddenField {
  field: string
  value: string
}

interface WrapMentionsParams {
  content: string
  hiddenFields?: HiddenField[]
  journeyItems?: FormJourneyItem[]
  noBadges?: boolean
  noUI?: boolean
}

/**
 * Wraps mentions (@score, @hiddenFields) and references ({{blockId}}) with styled spans for editor mode UI
 * @param content - The text content to process
 * @param hiddenFields - Array of hidden field objects with field and value
 * @param journeyItems - Array of journey items for {{blockId}} references
 * @returns Processed content with mentions wrapped in `label` / `label-gray` spans
 */
export function wrapMentionsWithBadges({
  content,
  hiddenFields = [],
  journeyItems = [],
  noBadges = false,
  noUI = false
}: WrapMentionsParams): string {
  let processedContent = content

  // Replace @score with badge
  processedContent = processedContent.replace(
    /@score/g,
    noBadges
      ? noUI ? '___' : '<span class="fillerBorder"></span>'
      : `<span 
      class="label label-gray vertical-offset"
      data-tooltip-id="global-tooltip"
      data-tooltip-content="This is a dynamic value based on the user's response."
    >
      score
    </span>`
  )

  // Replace hidden fields with badges
  hiddenFields.forEach(({ field }) => {
    // Ensure the field starts with @ for proper matching
    const fieldWithAt = field.startsWith('@') ? field : `@${field}`
    const escapedField = fieldWithAt.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const regex = new RegExp(escapedField, 'g')

    // Check if the field exists in content before replacing
    if (processedContent.includes(fieldWithAt)) {
      processedContent = processedContent.replace(
        regex,
        noBadges
          ? noUI ? '___' : '<span class="fillerBorder"></span>'
          : `<span 
          class="label label-gray vertical-offset"
          data-tooltip-id="global-tooltip"
          data-tooltip-content="This is a dynamic value from hidden fields. These are captured from the URL params."
        >
          ${field}
        </span>`
      )
    }
  })

  // Helper function to convert field names to title case
  const toTitleCase = (fieldName: string): string => {
    return fieldName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  // Handle journey item references {{blockId}} and {{blockId__fieldName}}
  const journeyItemPattern = /\{\{([^}]+)\}\}/g
  let match
  while ((match = journeyItemPattern.exec(processedContent)) !== null) {
    const reference = match[1]

    // Check if this is a subfield reference (contains __)
    if (reference.includes('__')) {
      const [blockId, fieldName] = reference.split('__')
      const journeyItem = journeyItems.find((item) => item.blockId === blockId)

      if (journeyItem) {
        const fieldDisplayName = toTitleCase(fieldName)
        const cleanedContent = journeyItem.content.replace(/\{\{[^}]+\}\}/g, '___')

        processedContent = processedContent.replace(
          match[0],
          noBadges
            ? noUI ? '___' : '<span class="fillerBorder"></span>'
            : `<span 
            class="label label-gray vertical-offset"
            data-tooltip-id="global-tooltip"
            data-tooltip-content="This is a dynamic reference to the ${fieldDisplayName} field from: ${cleanedContent}"
          >
            ${fieldDisplayName}
          </span>`
        )
      } else {
        processedContent = processedContent.replace(
          match[0],
          noBadges
            ? noUI ? '___' : '<span class="fillerBorder"></span>'
            : `<span 
            class="label label-red"
            data-tooltip-id="global-tooltip"
            data-tooltip-content="Unable to find referenced question. Please check if this question has been removed or update this value."
          >
            Missing Question
          </span>`
        )
      }
    } else {
      // Regular blockId reference
      const blockId = reference
      const journeyItem = journeyItems.find((item) => item.blockId === blockId)

      if (journeyItem) {
        // Replace {{}} references within the content with ___
        const cleanedContent = journeyItem.content.replace(/\{\{[^}]+\}\}/g, '___')
        const displayContent = truncate(cleanedContent, 30)

        processedContent = processedContent.replace(
          match[0],
          noBadges
            ? noUI ? displayContent : '<span class="fillerBorder"></span>'
            : `<span 
            class="label label-gray vertical-offset"
            data-tooltip-id="global-tooltip"
            data-tooltip-content="This is a dynamic reference to another question: ${cleanedContent}"
          >
            ${displayContent}
          </span>`
        )
      } else {
        processedContent = processedContent.replace(
          match[0],
          noBadges
            ? noUI ? '___' : '<span class="fillerBorder"></span>'
            : `<span 
            class="label label-red"
            data-tooltip-id="global-tooltip"
            data-tooltip-content="Unable to find referenced question. Please check if this question has been removed or update this value."
          >
            Missing Question
          </span>`
        )
      }
    }
  }

  return processedContent
}